package com.arenaproplay.admin.ui.components.forms

import androidx.compose.runtime.*
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.toRgbString
import com.arenaproplay.admin.ui.utils.Constants
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*

data class DropdownOption(
    val value: String,
    val label: String
)

@Composable
fun Dropdown(
    options: List<DropdownOption>,
    selectedValue: String?,
    onSelect: (DropdownOption) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "Select...",
    error: String? = null,
    enabled: Boolean = true
) {
    var isOpen by remember { mutableStateOf(false) }
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)
    val selectedOption = options.find { it.value == selectedValue }

    Column(modifier = modifier.fillMaxWidth().gap(4.px)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(14.px)
                    .color(colors.textSecondary)
                    .margin(bottom = 4.px)
            )
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Relative)
        ) {
            // Trigger
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .backgroundColor(colors.surface)
                    .borderRadius(8.px)
                    .padding(12.px)
                    .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
                    .opacity(if (enabled) 1.0 else 0.6)
                    .onClick { if (enabled) isOpen = !isOpen }
                    .styleModifier {
                        val borderColor = when {
                            error != null -> AppColors.Error.toRgbString()
                            isOpen -> AppColors.Primary.toRgbString()
                            else -> colors.border.toRgbString()
                        }
                        property("border", "1px solid $borderColor")
                        property("transition", "border-color 200ms ease")
                        property("box-sizing", "border-box")
                        property("user-select", "none")
                    }
            ) {
                SpanText(
                    text = selectedOption?.label ?: placeholder,
                    modifier = Modifier
                        .fontSize(16.px)
                        .color(
                            if (selectedOption != null) colors.textPrimary
                            else colors.textTertiary
                        )
                )

                // Arrow
                SpanText(
                    text = if (isOpen) "\u25B2" else "\u25BC",
                    modifier = Modifier
                        .position(Position.Absolute)
                        .right(12.px)
                        .top(50.percent)
                        .fontSize(10.px)
                        .color(colors.textSecondary)
                        .styleModifier {
                            property("transform", "translateY(-50%)")
                        }
                )
            }

            // Dropdown list
            if (isOpen) {
                Column(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .top(100.percent)
                        .left(0.px)
                        .right(0.px)
                        .margin(top = 4.px)
                        .backgroundColor(colors.surface)
                        .borderRadius(8.px)
                        .zIndex(100)
                        .overflow(Overflow.Auto)
                        .styleModifier {
                            property("max-height", "200px")
                            property("border", "1px solid ${colors.border.toRgbString()}")
                            property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.2)")
                        }
                ) {
                    options.forEach { option ->
                        val isSelected = option.value == selectedValue

                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(10.px, 12.px)
                                .cursor(Cursor.Pointer)
                                .backgroundColor(
                                    if (isSelected) AppColors.Primary.toRgbString().let {
                                        com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 162, 255, 0.1f)
                                    } else colors.surface
                                )
                                .transition(
                                    Transition.of("background-color", Constants.ANIMATION_FAST.ms)
                                )
                                .onClick {
                                    onSelect(option)
                                    isOpen = false
                                }
                                .styleModifier {
                                    property("border-bottom", "1px solid ${colors.border.toRgbString()}")
                                }
                        ) {
                            SpanText(
                                text = option.label,
                                modifier = Modifier
                                    .fontSize(14.px)
                                    .color(
                                        if (isSelected) AppColors.Primary
                                        else colors.textPrimary
                                    )
                            )
                        }
                    }
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(12.px)
                    .color(AppColors.Error)
                    .margin(top = 4.px)
            )
        }
    }

    // Close dropdown when clicking outside
    if (isOpen) {
        Box(
            modifier = Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px).right(0.px).bottom(0.px)
                .zIndex(99)
                .onClick { isOpen = false }
                .styleModifier {
                    property("background", "transparent")
                }
        )
    }
}
